import { downloadFile, type PmsSource, publishedInstructions } from '@felix/ui';
import { officeApi, pmsReportFile } from './api.js';
import { tusUpload } from './tus-upload.js';

/** CM dosyası yüklemesinin amacı (`apps/server/src/office/pms-cm.ts`). */
export const CM_UPLOAD_PURPOSE = 'cm_import';

/**
 * Ofiste PMS ekipman ana verisi (M7a; ADR 0029): kurulum devrinde (gemi yazarı `cloud`) ofis
 * ağacı hazırlar; etkinleşmiş gemide ana veriyi yalnız gemi yazar (K1), ekran salt okunurdur ve
 * "Edited on board" gösterir. Sayaç okuması ofiste girilmez (K13): `counter` yok.
 */
export const officePmsSource: PmsSource = {
  scope: 'web',
  vessels: () => officeApi.vessels(),
  particulars: (vesselId) => officeApi.particulars(vesselId),
  tree: (vesselId) => officeApi.equipmentTree(vesselId),
  equipment: (id) => officeApi.equipment(id),
  readings: (id) => officeApi.equipmentReadings(id),
  // Dolaşan bileşenler (DNV-CP-0206 4.3 c; ADR 0133 §6): gemi verisi, salt okunur
  rotables: (vesselId) => officeApi.pmsRotables(vesselId),
  editor: {
    create: (vesselId, input) => officeApi.createEquipment(vesselId, input),
    update: (id, patch) => officeApi.updateEquipment(id, patch),
    decommission: (id, rowVersion, reason) =>
      officeApi.decommissionEquipment(id, rowVersion, reason),
    loadDefaultStructure: (vesselId) => officeApi.loadDefaultStructure(vesselId),
  },
  // M7b3: plan, iş emirleri ve geçmiş ofiste salt okunur; iş tanımı kurulum devrinde (K1);
  // bakım kaydı ofiste yazılmaz (K13): `work` yok. Ofis adımı kararı parolayla (K10, K11).
  jobs: (vesselId) => officeApi.pmsJobs(vesselId),
  plan: (vesselId, range) => officeApi.pmsPlan(vesselId, range),
  workOrders: (vesselId) => officeApi.pmsWorkOrders(vesselId),
  workOrder: (id) => officeApi.pmsWorkOrder(id),
  history: async (vesselId, filter) => (await officeApi.pmsHistory(vesselId, filter)).items,
  attachments: (workOrderId, ownerType) => officeApi.pmsAttachments(workOrderId, ownerType),
  attachmentUrl: (id) => `/api/office/attachments/${encodeURIComponent(id)}`,
  pending: async () => (await officeApi.pmsPending()).items,
  intervalHistory: async (jobId) => (await officeApi.pmsIntervalHistory(jobId)).items,
  // Aralık ve talimat değişiklikleri rolüyle (DNV-CP-0206 Sec.2 [4.6] b; ADR 0133 §2)
  jobChangeLog: async (jobId) => (await officeApi.pmsIntervalHistory(jobId)).changes ?? [],
  // Ertelemeler (ADR 0108 §4): `vesselId` yoksa filo
  postponements: async (vesselId) => (await officeApi.pmsPostponements(vesselId)).items,
  // Kurulum değişiklikleri (ADR 0142 §3): Fleet ▸ Vessels ▸ Setup Changes ve PMS şeridi
  setupChanges: (vesselId, query) => officeApi.pmsSetupChanges(vesselId, query),
  instructions: async () => publishedInstructions((await officeApi.pmsInstructionTree()).documents),
  jobEditor: {
    create: (vesselId, equipmentId, input) =>
      officeApi.createPmsJob(vesselId, { equipmentId, ...input }),
    update: (id, rowVersion, patch) => officeApi.updatePmsJob(id, { rowVersion, ...patch }),
    remove: (id, rowVersion, reason) => officeApi.removePmsJob(id, rowVersion, reason),
  },
  office: {
    decidePostponement: (id, input) => officeApi.decidePostponement(id, input),
    decideIntervalChange: (id, input) => officeApi.decideIntervalChange(id, input),
    // Etkin gemide aralığı ofis girer (ADR 0104): ölçüm noktaları gemide kalır
    sendIntervalChange: (jobId, input) =>
      officeApi.sendIntervalChange(jobId, {
        ...input,
        interval: { calendar: input.interval.calendar, runningHours: input.interval.runningHours },
      }),
    // Breakdown kapanışının süperintendent adımı (K24)
    decideDefect: (id, input) => officeApi.decideDefect(id, input),
    // Ertelemeye ofis notu ya da kararı (ADR 0108 §3): gemiye iner, gemi uygular
    postponementAction: (id, input) => officeApi.postponementAction(id, { ...input }),
  },
  // M7c: ölçüm noktası kurulum devrinde ofiste yazılır (K1); ölçüm ve arıza gemide (K13):
  // `defectEditor` yok
  measurementPoints: (vesselId, filter) => officeApi.pmsMeasurementPoints(vesselId, filter),
  measurements: async (vesselId, filter) =>
    (await officeApi.pmsMeasurements(vesselId, filter)).items,
  pointEditor: {
    create: (vesselId, input) => officeApi.createMeasurementPoint(vesselId, { ...input }),
    update: (id, rowVersion, patch) =>
      officeApi.updateMeasurementPoint(id, { rowVersion, ...patch }),
    remove: (id, rowVersion, reason) => officeApi.removeMeasurementPoint(id, rowVersion, reason),
  },
  // Kurulum devrinde yalnız eşik (K20): dosya bir kez yüklenir, önizleme ve uygulama aynı yüklemeyle
  cm: {
    preview: async (vesselId, file) => {
      const uploadId = await tusUpload(file, { purpose: CM_UPLOAD_PURPOSE, onProgress: () => {} });
      const body = { vesselId, uploadId, fileName: file.name };
      const preview = await officeApi.pmsCmImport({ ...body, preview: true });
      return {
        fileName: file.name,
        report: preview.report,
        apply: async () => ({
          report: (await officeApi.pmsCmImport({ ...body, preview: false })).report,
        }),
      };
    },
  },
  defects: (vesselId, filter) => officeApi.pmsDefects(vesselId, filter),
  defect: (id) => officeApi.pmsDefect(id),
  alerts: (vesselId) => officeApi.pmsAlerts(vesselId),
  // Settings ▸ PMS (K32): şirket ayarı ofiste düzenlenir
  settings: () => officeApi.pmsSettings(),
  settingsEditor: {
    save: (rowVersion, values) => officeApi.savePmsSettings({ rowVersion, ...values }),
  },
  // M7e: NR496 raporları; Excel ve CSV sunucuda üretilir, tarayıcı indirir (plan Task 29)
  report: (vesselId, reportId, query) => officeApi.pmsReport(vesselId, reportId, query),
  auditTrail: (vesselId, filter) => officeApi.auditTrail(vesselId, filter),
  exportReport: async (vesselId, reportId, options) => {
    const file = await pmsReportFile(vesselId, reportId, options);
    downloadFile(file.fileName, file.bytes, file.type);
  },
  // Rapor PDF'i tarayıcıda (Task 31) [NR496 4.1.2]: sürüm, basan kişi, şirket markası. Oturum
  // yanıtı rol taşımaz (ofis kullanıcısı; Shell ile aynı): rol boş
  reportPrint: async () => {
    const [me, branding] = await Promise.all([
      officeApi.me().catch(() => null),
      officeApi.branding().catch(() => null),
    ]);
    return {
      version: __APP_VERSION__,
      name: me?.user.fullName ?? me?.user.email ?? '',
      role: null,
      brand: branding?.branding?.name ?? null,
    };
  },
};
